/* AME PDP commerce engine — single pricing/config state store for the
 * Customize & Buy panel (snippets/ame-product-customize.liquid).
 *
 * Replaces the former inline IIFE. One source of truth:
 *   - the JSON island [data-ame-pdp-data] (variant prices/availability, config
 *     flags) rendered by snippets/ame-pdp-data.liquid;
 *   - Liquid-rendered data-* attributes on option controls, read ONCE at init
 *     (integer cents everywhere — never parsed from displayed text).
 *
 * All money is integer cents. The ONE formatter is AmeMoney.format().
 * Every recompute dispatches a bubbling "ame:pdp:state" CustomEvent so
 * decoupled consumers (hero price, Bread placement, sticky CTA) can subscribe
 * without reaching into this panel's DOM.
 *
 * Cart contract (asserted by tests/p3-browser — do not rename):
 *   properties _ame_config_id / _ame_addon_type (addon|pride_service|model_config)
 *   / _ame_parent_variant_id / _Parent product handle / _Added as accessory /
 *   _ame_service_term; one atomic multi-line POST to /cart/add.js.
 */
(function () {
  'use strict';

  var panel = document.querySelector('[data-customize-panel]');
  if (!panel) return;

  var islandEl = panel.querySelector('[data-ame-pdp-data]');
  var island = {};
  try { island = JSON.parse((islandEl && islandEl.textContent) || '{}'); } catch (_) { island = {}; }
  var variants = island.variants || {};
  var parentHandle = island.product ? island.product.handle : '';

  /* ---- money ---- */
  var moneyFormat = island.moneyFormat || '${{amount}}';
  window.AmeMoney = window.AmeMoney || {
    format: function (cents) {
      var n = cents / 100;
      return moneyFormat
        .replace('{{amount}}', n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }))
        .replace('{{amount_no_decimals}}', Math.round(n).toLocaleString('en-US'));
    }
  };
  var fmt = window.AmeMoney.format;

  /* ---- element refs (existing DOM contract) ---- */
  var form          = document.getElementById('ame-product-form');
  var variantSelect = panel.querySelector('[data-variant-select]');
  var selects       = Array.prototype.slice.call(panel.querySelectorAll('[data-addon-select]'));
  var checkboxes    = Array.prototype.slice.call(panel.querySelectorAll('[data-addon-checkbox]'));
  var totalAmountEl = panel.querySelector('[data-customize-total-amount]');
  var qtyInput      = panel.querySelector('[data-quantity-input]');
  var errorsEl      = panel.querySelector('[data-validation-messages]');
  var summaryEl     = panel.querySelector('[data-order-summary]');
  var summaryLines  = panel.querySelector('[data-order-summary-lines]');
  var atcBtn        = form ? form.querySelector('button[type="submit"][name="add"]') : null;

  var fabricPicker       = panel.querySelector('[data-fabric-picker]');
  var fabricTrigger      = panel.querySelector('[data-fabric-picker-trigger]');
  var fabricPanel        = panel.querySelector('[data-fabric-picker-panel]');
  var fabricLabel        = panel.querySelector('[data-fabric-picker-label]');
  var fabricSwatchPrev   = panel.querySelector('[data-fabric-picker-swatch]');
  var fabricNameEl       = panel.querySelector('[data-fabric-selected-name]');
  var fabricCollectionEl = panel.querySelector('[data-fabric-selected-collection]');
  var fabricUpchargeEl   = panel.querySelector('[data-fabric-selected-upcharge]');
  var fabricVariantIdEl  = panel.querySelector('[data-fabric-selected-variant-id]');

  /* ---- state ---- */
  var state = {
    selectedVariantId: island.initialVariantId ? String(island.initialVariantId) : null,
    quantity: 1,
    fabric: null,          // { name, collection, upchargeCents, variantId, chargeable }
    derived: {}
  };

  function variantInfo(id) { return id ? variants[String(id)] : null; }

  /* Read the current picks from the controls. Controls are input devices; the
     prices come from their Liquid-rendered data-price attributes (cents). */
  function selectedAddOns() {
    var dropdownPicks = selects.map(function (sel) {
      var opt = sel.options[sel.selectedIndex];
      // Size-excluded option (custom.option_size_exclusions): never a pick, whatever the DOM says.
      if (opt && optionExcludedForCurrentSize(opt)) return { variantId: '0', priceCents: 0, key: sel.getAttribute('data-addon-key'), title: '', kind: 'select' };
      if (sel._ameCompatOk && !sel._ameCompatOk()) return { variantId: '0', priceCents: 0, key: sel.getAttribute('data-addon-key'), title: '', kind: 'select' };
      return {
        variantId: sel.value,
        priceCents: opt ? (parseInt(opt.getAttribute('data-price') || '0', 10) || 0) : 0,
        key: sel.getAttribute('data-addon-key'),
        title: opt ? (opt.getAttribute('data-title') || (opt.textContent || '').trim()) : '',
        kind: 'select'
      };
    }).filter(function (a) { return a.variantId && a.variantId !== '0' && String(a.variantId).indexOf('prop:') !== 0; });
    var checkboxPicks = checkboxes
      .filter(function (b) { return b.getAttribute('aria-pressed') === 'true'; })
      .map(function (b) {
        return {
          variantId: b.getAttribute('data-variant-id'),
          priceCents: parseInt(b.getAttribute('data-price') || '0', 10) || 0,
          key: b.getAttribute('data-addon-key'),
          title: b.getAttribute('data-title') || '',
          kind: 'checkbox'
        };
      })
      .filter(function (a) { return a.variantId; });
    var radioPicks = Array.prototype.slice.call(panel.querySelectorAll('[data-addon-radio]:checked'))
      .filter(function (r) { return r.getAttribute('data-variant-id'); })
      .map(function (r) {
        return {
          variantId: r.getAttribute('data-variant-id'),
          priceCents: parseInt(r.getAttribute('data-price') || '0', 10) || 0,
          key: 'addon',
          title: r.getAttribute('data-title') || '',
          groupLabel: r.getAttribute('data-group-label') || '',
          kind: 'radio'
        };
      });
    return dropdownPicks.concat(checkboxPicks).concat(radioPicks);
  }

  function validationErrors() {
    var errs = [];
    var v = variantInfo(state.selectedVariantId);
    if (!state.selectedVariantId) {
      errs.push({ code: 'no_combo', message: 'This combination isn’t available — please pick different options.' });
    } else if (v && v.available === false) {
      errs.push({ code: 'sold_out', message: 'The selected configuration is sold out.' });
    }
    // A size-excluded FABRIC still selected (only reachable by bypassing the UI sync) blocks the submit too.
    // ★ read the DOM inputs too: a bypass that writes the hidden upholstery input directly never touches state.fabric.
    var fabName = (state.fabric && state.fabric.name) || (fabricNameEl && fabricNameEl.value) || '';
    if (fabName) {
      var exF = fabricExcludedForCurrentSize(fabName);
      if (exF) errs.push({ code: 'excluded_option', message: exF });
    }
    // A size-excluded option still selected in the DOM (only reachable by bypassing the UI sync)
    // blocks the submit outright: the cart must never carry a combination the manufacturer excludes.
    selects.forEach(function (sel) {
      var opt = sel.options[sel.selectedIndex];
      if (opt && sel.value && sel.value !== '0' && optionExcludedForCurrentSize(opt)) {
        errs.push({ code: 'excluded_option', message: opt.getAttribute('data-excluded-message') || ((opt.getAttribute('data-title') || 'That option') + ' is not available on the selected size.') });
      }
    });
    return errs;
  }

  /* ---- size-conditional option exclusions (custom.option_size_exclusions) ----
     Declared per <option> by snippets/ame-option-size-exclusion.liquid. Identity is the host
     VARIANT id (what the cart will actually carry); the size title is the fallback so a renamed
     option value fails towards the footrest gate's behaviour rather than silently un-gating. */
  function currentSizeTitleSafe() {
    if (variantSelect && variantSelect.tagName === 'SELECT') {
      var o = variantSelect.options[variantSelect.selectedIndex];
      return o ? (o.getAttribute('data-size-title') || '') : '';
    }
    var card = panel.querySelector('[data-variant-card-radio]:checked');
    return card ? (card.getAttribute('data-size-title') || '') : '';
  }
  function optionExcludedForCurrentSize(opt) {
    var idsAttr = opt.getAttribute('data-excluded-variant-ids');
    var sizesAttr = opt.getAttribute('data-excluded-sizes');
    if (!idsAttr && !sizesAttr) return false;
    var vid = String(state.selectedVariantId || (variantSelect && variantSelect.value) || '');
    if (idsAttr && vid) {
      var ids = idsAttr.split(',').map(function (x) { return x.trim(); }).filter(Boolean);
      if (ids.indexOf(vid) !== -1) return true;
    }
    if (sizesAttr) {
      var size = currentSizeTitleSafe();
      var sizes = sizesAttr.split('|').map(function (x) { return x.trim(); }).filter(Boolean);
      if (size && sizes.indexOf(size) !== -1) return true;
    }
    return false;
  }
  function syncOptionExclusions() {
    var ruled = Array.prototype.slice.call(panel.querySelectorAll('select[data-addon-select] option[data-excluded-variant-ids], select[data-addon-select] option[data-excluded-sizes]'));
    if (!ruled.length) return;
    var notes = {};
    ruled.forEach(function (opt) {
      var sel = opt.closest('select'); if (!sel) return;
      var excluded = optionExcludedForCurrentSize(opt);
      opt.disabled = excluded || opt.hasAttribute('data-unavailable');
      if (!excluded) return;
      var msg = opt.getAttribute('data-excluded-message') || ((opt.getAttribute('data-title') || 'This option') + ' is not available on the selected size.');
      if (sel.value === opt.value) {
        // The customer's earlier choice just became invalid on a size change: clear it, recalc, say why.
        sel.value = '0';
        sel.dispatchEvent(new Event('change', { bubbles: true }));
        msg += ' It has been removed from your configuration and the price updated.';
      }
      notes[sel.id || sel.getAttribute('data-addon-key')] = { sel: sel, msg: msg };
    });
    selects.forEach(function (sel) {
      var row = sel.closest('[data-option-row], .ame-customize__row');
      var note = row ? row.querySelector('[data-option-rule-note]') : null;
      if (!note) return;
      var n = notes[sel.id || sel.getAttribute('data-addon-key')];
      if (n) { note.textContent = n.msg; note.hidden = false; }
      else if (!note.hidden) { note.textContent = ''; note.hidden = true; }
    });
  }
  /* Fabric rules (option_key "fabric"): the same attributes on every [data-fabric-option] — in the picker
     panel AND the swatch-browser copies. Returns the rule message when the named fabric is excluded on
     the current size, else ''. */
  function fabricExcludedForCurrentSize(name) {
    if (!name) return '';
    var els = document.querySelectorAll('[data-fabric-option][data-fabric-name="' + name.replace(/"/g, '\\"') + '"]');
    for (var i = 0; i < els.length; i++) {
      if (optionExcludedForCurrentSize(els[i])) return els[i].getAttribute('data-excluded-message') || (name + ' is not available on the selected size.');
    }
    return '';
  }
  function clearFabricSelection() {
    state.fabric = null;
    if (fabricNameEl) fabricNameEl.value = '';
    if (fabricCollectionEl) fabricCollectionEl.value = '';
    if (fabricUpchargeEl) fabricUpchargeEl.value = '0';
    if (fabricVariantIdEl) fabricVariantIdEl.value = '';
    if (fabricLabel) fabricLabel.textContent = 'Select upholstery';
    if (fabricSwatchPrev) fabricSwatchPrev.style.backgroundImage = '';
    document.querySelectorAll('[data-fabric-option][aria-selected="true"]').forEach(function (el) { el.setAttribute('aria-selected', 'false'); });
  }
  function syncFabricExclusions() {
    var ruled = document.querySelectorAll('[data-fabric-option][data-excluded-variant-ids], [data-fabric-option][data-excluded-sizes]');
    if (!ruled.length) return;
    var note = panel.querySelector('[data-fabric-rule-note]');
    var hiddenNames = [];
    ruled.forEach(function (el) {
      var ex = optionExcludedForCurrentSize(el);
      el.hidden = ex; el.setAttribute('aria-disabled', ex ? 'true' : 'false'); el.disabled = ex;
      if (ex) { var n = el.getAttribute('data-fabric-name'); if (hiddenNames.indexOf(n) === -1) hiddenNames.push(n); }
    });
    var msg = '';
    var curName = (state.fabric && state.fabric.name) || (fabricNameEl && fabricNameEl.value) || '';
    if (curName) {
      msg = fabricExcludedForCurrentSize(curName);
      if (msg) { clearFabricSelection(); msg += ' Your upholstery choice has been cleared and the price updated.'; }
    }
    if (!msg && hiddenNames.length) {
      var any = document.querySelector('[data-fabric-option][data-fabric-name="' + hiddenNames[0].replace(/"/g, '\\"') + '"]');
      msg = (any && any.getAttribute('data-excluded-message')) || (hiddenNames.join(', ') + ' not available on the selected size.');
    }
    if (note) { note.textContent = msg; note.hidden = !msg; }
  }
  document.addEventListener('ame:variant-change', function () { syncOptionExclusions(); syncFabricExclusions(); recompute(); });

  function recompute() {
    var v = variantInfo(state.selectedVariantId);
    var baseCents = v ? v.priceCents : 0;
    var picks = selectedAddOns();
    var fabricCents = (state.fabric && state.fabric.chargeable) ? state.fabric.upchargeCents : 0;
    // Configuration children (selects/radios + fabric) scale with quantity;
    // discrete accessory checkboxes stay qty 1 per toggle.
    var perUnitCents = picks.reduce(function (s, a) { return s + (a.kind === 'checkbox' ? 0 : a.priceCents); }, 0) + fabricCents;
    var accessoryCents = picks.reduce(function (s, a) { return s + (a.kind === 'checkbox' ? a.priceCents : 0); }, 0);
    var errs = validationErrors();
    state.derived = {
      basePriceCents: baseCents,
      compareAtCents: v ? (v.compareAtCents || 0) : 0,
      addonTotalCents: perUnitCents + accessoryCents,
      configuredTotalCents: (baseCents + perUnitCents) * state.quantity + accessoryCents,
      available: !!(v && v.available !== false),
      validationErrors: errs,
      canAddToCart: !!(v && v.available !== false) && errs.length === 0,
      picks: picks
    };
    render();
    document.dispatchEvent(new CustomEvent('ame:pdp:state', {
      bubbles: false,
      detail: {
        productId: island.product ? island.product.id : null,
        variantId: state.selectedVariantId,
        quantity: state.quantity,
        basePriceCents: state.derived.basePriceCents,
        configuredTotalCents: state.derived.configuredTotalCents,
        available: state.derived.available,
        selectionCount: (state.derived.picks || []).length + (state.fabric && state.fabric.name ? 1 : 0),
        missingRequired: missingRequired().map(function (m) { return m.label; })
      }
    }));
    saveState();
  }

  /* ---- required choices still open (2026-10-01) ----
     A READ-ONLY mirror of the submit guard's checks, in the same order, for the status line at the top of the panel
     and the phone bar ("what remains required"). The submit handler below stays the only thing that blocks a cart
     add; when a check is added there, add it here. Labels are display names, never option values. */
  function missingRequired() {
    var out = [];
    Array.prototype.forEach.call(panel.querySelectorAll('[data-addon-required="true"]'), function (el) {
      if (el.getAttribute('aria-pressed') !== 'true') out.push({ el: el, label: el.getAttribute('data-title') || 'Required option' });
    });
    if (fabricPicker && !((fabricNameEl && fabricNameEl.value) || '')) out.push({ el: fabricTrigger, label: 'Upholstery' });
    Array.prototype.forEach.call(panel.querySelectorAll('select[data-addon-required-group="true"]'), function (el) {
      if (!el.value) out.push({ el: el, label: el.getAttribute('data-addon-required-label') || el.getAttribute('data-group-label') || 'Required option' });
    });
    Array.prototype.forEach.call(panel.querySelectorAll('select[data-required-choice="true"]'), function (el) {
      if (!el.value) out.push({ el: el, label: (el.getAttribute('data-required-choice-label') || 'Seat Type').replace(/^an? /i, '') });
    });
    Array.prototype.forEach.call(panel.querySelectorAll('[data-lineitem-select][data-lineitem-required="true"]'), function (el) {
      if (!el.value) out.push({ el: el, label: el.getAttribute('data-lineitem-label') || 'Required option' });
    });
    return out;
  }
  var requiredControlCount = panel.querySelectorAll('[data-addon-required="true"], select[data-addon-required-group="true"], select[data-required-choice="true"], [data-lineitem-select][data-lineitem-required="true"]').length + (fabricPicker ? 1 : 0);
  var reqStatusEl = null;
  function renderRequired() {
    if (!requiredControlCount) return;
    if (!reqStatusEl) {
      reqStatusEl = document.createElement('p');
      reqStatusEl.className = 'ame-customize__reqstatus';
      reqStatusEl.setAttribute('data-required-status', '');
      reqStatusEl.setAttribute('role', 'status');
      var anchor = (form || panel).querySelector('.ame-customize__section-divider, .ame-customize__row');
      if (anchor && anchor.parentNode) anchor.parentNode.insertBefore(reqStatusEl, anchor); else panel.appendChild(reqStatusEl);
    }
    var miss = missingRequired();
    var text = miss.length ? 'Still to choose: ' + miss.map(function (m) { return m.label; }).join(', ') : 'Required choices complete';
    reqStatusEl.classList.toggle('is-complete', !miss.length);
    if (reqStatusEl.textContent !== text) reqStatusEl.textContent = text;   // no change, no announcement
  }
  var smooth = function () { return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'; };
  // The phone bar asks the engine to take the shopper to the first open required choice (assets/ame-sticky-mobile-cta).
  document.addEventListener('ame:pdp:focus-required', function () {
    var miss = missingRequired(); if (!miss.length) return;
    var el = miss[0].el; if (!el) return;
    var det = el.closest && el.closest('details');
    while (det) { det.open = true; det = det.parentElement && det.parentElement.closest('details'); }
    var row = (el.closest && el.closest('[data-option-row], .ame-customize__row, .ame-fabric-picker')) || el;
    row.scrollIntoView({ behavior: smooth(), block: 'center' });
    if (el.focus) el.focus({ preventScroll: true });
  });
  var track = function (name, data) { try { if (window.ameTrack) window.ameTrack(name, data || {}); } catch (_) { /* analytics never breaks commerce */ } };

  /* ---- render subscribers ---- */
  function render() {
    var d = state.derived;
    if (totalAmountEl) totalAmountEl.textContent = fmt(d.configuredTotalCents);
    renderOrderSummary();
    renderAccSummaries();
    renderAtc();
    renderErrors();
    renderHero();
    renderRequired();
  }

  function renderOrderSummary() {
    if (!summaryEl || !summaryLines) return;
    var d = state.derived;
    var v = variantInfo(state.selectedVariantId);
    var rows = [];
    var qtySuffix = state.quantity > 1 ? ' × ' + state.quantity : '';
    rows.push({ kind: 'base', label: 'Base Price' + (v && v.title && v.title !== 'Default Title' ? ' (' + v.title + ')' : '') + qtySuffix,
                amount: v ? v.priceCents * state.quantity : 0 });
    if (state.fabric && state.fabric.name) {
      rows.push({ kind: 'fabric', label: 'Upholstery: ' + state.fabric.name,
                  amount: state.fabric.chargeable ? state.fabric.upchargeCents * state.quantity : 0,
                  included: !(state.fabric.upchargeCents > 0) });
    }
    d.picks.forEach(function (a) {
      var kind = a.key === 'service' ? 'service' : a.key === 'delivery' ? 'delivery' : 'addon';
      var mult = a.kind === 'checkbox' ? 1 : state.quantity;
      rows.push({ kind: kind, label: a.title || 'Option', amount: a.priceCents * mult });
    });
    // Included defaults worth naming: delivery select present but unselected.
    var dsel = panel.querySelector('select[data-addon-key="delivery"]');
    if (dsel && (!dsel.value || dsel.value === '0')) {
      rows.push({ kind: 'delivery', label: 'Free Curbside Delivery', amount: 0, included: true });
    } else if (!dsel && island.deliveryIncludedLabel) {
      rows.push({ kind: 'delivery', label: island.deliveryIncludedLabel, amount: 0, included: true });
    }
    var configured = rows.length > 1 || state.quantity > 1;
    summaryEl.hidden = !configured;
    if (!configured) { summaryLines.innerHTML = ''; return; }
    var html = rows.map(function (r) {
      return '<li class="ame-summary__line" data-summary-line="' + r.kind + '">' +
        '<span class="ame-summary__label"></span>' +
        '<span class="ame-summary__amount">' + (r.included ? 'Included' : fmt(r.amount)) + '</span></li>';
    }).join('');
    html += '<li class="ame-summary__line ame-summary__line--muted" data-summary-line="tax">' +
      '<span class="ame-summary__label">Est. Tax</span><span class="ame-summary__amount">— calculated at checkout</span></li>';
    html += '<li class="ame-summary__line ame-summary__line--total" data-summary-line="total">' +
      '<span class="ame-summary__label">Total</span><span class="ame-summary__amount">' + fmt(d.configuredTotalCents) + '</span></li>';
    summaryLines.innerHTML = html;
    // Labels set via textContent so metafield-sourced titles can't inject markup.
    var labelEls = summaryLines.querySelectorAll('.ame-summary__label');
    rows.forEach(function (r, i) { if (labelEls[i]) labelEls[i].textContent = r.label; });
  }

  function renderAccSummaries() {
    panel.querySelectorAll('[data-acc-details]').forEach(function (det) {
      var picked = Array.prototype.slice.call(det.querySelectorAll('[data-addon-checkbox][aria-pressed="true"]'));
      var out = det.querySelector('[data-acc-summary]');
      if (!out) return;
      if (!picked.length) { out.textContent = ''; return; }
      var cents = picked.reduce(function (s, b) { return s + (parseInt(b.getAttribute('data-price') || '0', 10) || 0); }, 0);
      out.textContent = picked.length + ' selected · +' + fmt(cents);
    });
  }

  function renderAtc() {
    if (!atcBtn) return;
    var d = state.derived;
    atcBtn.disabled = !d.canAddToCart;
    if (!atcBtn.dataset.adding) {
      atcBtn.textContent = d.canAddToCart ? 'Add to Cart'
        : (d.validationErrors.some(function (e) { return e.code === 'no_combo' || e.code === 'excluded_option'; }) ? 'Unavailable Combination' : 'Sold Out');
    }
  }

  function renderErrors() {
    if (!errorsEl) return;
    var errs = state.derived.validationErrors;
    if (!errs.length) { errorsEl.hidden = true; errorsEl.textContent = ''; return; }
    errorsEl.hidden = false;
    errorsEl.textContent = errs.map(function (e) { return e.message; }).join(' ');
  }

  /* Hero price block (sections/ame-product-info.liquid) tracks the BASE variant
     price — the configured total lives in this panel's Total + Order Summary. */
  function renderHero() {
    var d = state.derived;
    var heroPrice = document.querySelector('[data-product-price]');
    if (heroPrice && d.basePriceCents) heroPrice.textContent = fmt(d.basePriceCents);
    var heroCompare = document.querySelector('[data-product-compare-price]');
    var heroBadge = document.querySelector('[data-product-save-badge]');
    if (heroCompare) {
      var show = d.compareAtCents > d.basePriceCents;
      heroCompare.hidden = !show;
      if (show) heroCompare.textContent = fmt(d.compareAtCents);
      if (heroBadge) {
        heroBadge.hidden = !show;
        if (show) heroBadge.textContent = 'Save ' + Math.round((1 - d.basePriceCents / d.compareAtCents) * 100) + '%';
      }
    }
    var skuEl = document.querySelector('[data-product-sku]');
    var v = variantInfo(state.selectedVariantId);
    if (skuEl && v) {
      if (v.sku) { skuEl.hidden = false; skuEl.textContent = '(Model No. ' + v.sku + ')'; }
      else skuEl.hidden = true;
    }
  }

  /* ---- variant selection ---- */
  function setVariant(id) {
    state.selectedVariantId = id ? String(id) : null;
    var hidden = form ? form.querySelector('[name="id"]') : null;
    if (hidden && id) {
      hidden.value = id;
      /* ★ A programmatic value assignment fires NO change event, so every consumer that
         listens on the hidden input (the gallery's variant→image switch) went deaf on the
         color-chip and variant-card paths — the exact "color doesn't change the picture"
         defect. The SELECT axis worked only because the USER's own change event fired.
         Guard: never re-dispatch on a SELECT — its change listener calls setVariant and
         would loop. */
      if (hidden.tagName !== 'SELECT') {
        hidden.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
    document.dispatchEvent(new CustomEvent('ame:variant-change', { detail: { id: id ? String(id) : null } }));
    recompute();
  }

  // Single-select mode: <select name="id" data-variant-select>
  if (variantSelect && variantSelect.tagName === 'SELECT') {
    variantSelect.addEventListener('change', function () { setVariant(variantSelect.value); });
  }

  // Variant selection cards: single-axis products with few variants render
  // radio cards; the hidden [data-variant-select] input still carries the id.
  var variantCards = panel.querySelector('[data-variant-cards]');
  if (variantCards && variantSelect && variantSelect.tagName === 'INPUT') {
    var cardRadios = Array.prototype.slice.call(variantCards.querySelectorAll('[data-variant-card-radio]'));
    var syncVariantCards = function () {
      cardRadios.forEach(function (r) {
        var lbl = variantCards.querySelector('label[for="' + r.id + '"]');
        if (lbl) lbl.classList.toggle('is-selected', r.checked);
      });
    };
    cardRadios.forEach(function (r) {
      r.addEventListener('change', function () {
        if (!r.checked) return;
        syncVariantCards();
        setVariant(r.value);
      });
    });
    syncVariantCards();
  }

  // Independent option axes (2026-09-18): one radio-card group per option, resolved through the same
  // position-ordered "i1|i2|i3" map. The customer decides Battery and Seat separately; the pair maps to
  // the one Shopify variant that carries both, so SKU / price / inventory semantics are unchanged.
  var axisGroups = Array.prototype.slice.call(panel.querySelectorAll('[data-axis-cards]'))
    .sort(function (a, b) {
      return (parseInt(a.getAttribute('data-option-position'), 10) || 0) -
             (parseInt(b.getAttribute('data-option-position'), 10) || 0);
    });
  if (axisGroups.length && variantSelect && variantSelect.tagName === 'INPUT') {
    var axisMap = island.optionIndexMap || {};
    var axisKey = function (override) {
      var parts = axisGroups.map(function (g, gi) {
        if (override && override.g === gi) return override.idx;
        var r = g.querySelector('[data-axis-radio]:checked');
        return r ? (r.getAttribute('data-idx') || '0') : '0';
      });
      while (parts.length < 3) parts.push('0');
      return parts.join('|');
    };
    var resolveAxes = function () {
      axisGroups.forEach(function (g, gi) {
        Array.prototype.forEach.call(g.querySelectorAll('[data-axis-radio]'), function (r) {
          var lbl = g.querySelector('label[for="' + r.id + '"]');
          // a value is selectable when the variant for (this value + the other axes' current picks) exists
          var v = axisMap[axisKey({ g: gi, idx: r.getAttribute('data-idx') || '0' })];
          var unavailable = !v || v.available === false;
          if (!r.hasAttribute('data-axis-static-disabled')) r.disabled = !v;
          if (lbl) { lbl.classList.toggle('is-selected', r.checked); lbl.classList.toggle('is-unavailable', unavailable && !r.checked); }
        });
      });
      var cur = axisMap[axisKey()];
      setVariant(cur ? cur.id : null);
    };
    axisGroups.forEach(function (g) {
      Array.prototype.forEach.call(g.querySelectorAll('[data-axis-radio]'), function (r) {
        if (r.disabled) r.setAttribute('data-axis-static-disabled', '');
        r.addEventListener('change', resolveAxes);
      });
    });
    resolveAxes();
  }

  // Color swatch mode: chips + per-option selects resolved through the
  // position-ordered "i1|i2|i3" map (island.optionIndexMap).
  var colorPicker = panel.querySelector('[data-color-picker]');
  if (colorPicker && variantSelect && variantSelect.tagName === 'INPUT') {
    var indexMap = island.optionIndexMap || {};
    var colorRadios  = Array.prototype.slice.call(panel.querySelectorAll('[data-color-radio]'));
    var extraSelects = Array.prototype.slice.call(panel.querySelectorAll('[data-extra-option]'))
      .sort(function (a, b) {
        return (parseInt(a.getAttribute('data-option-position'), 10) || 0) -
               (parseInt(b.getAttribute('data-option-position'), 10) || 0);
      });
    var colorPos = parseInt(variantSelect.getAttribute('data-color-position') || '1', 10) || 1;
    var optionCount = parseInt(variantSelect.getAttribute('data-option-count') || '1', 10) || 1;

    var selectedColorIdx = function () {
      var r = colorRadios.filter(function (x) { return x.checked; })[0];
      return r ? (r.getAttribute('data-idx') || '0') : '0';
    };
    var existsWithPrefix = function (prefix) {
      var p = prefix.join('|');
      for (var k in indexMap) { if (k === p || k.indexOf(p + '|') === 0) return true; }
      return false;
    };
    var selectAtPosition = function (pos) {
      return extraSelects.filter(function (s) {
        return (parseInt(s.getAttribute('data-option-position'), 10) || 0) === pos;
      })[0] || null;
    };
    var updateAvailability = function () {
      // Cascade left→right by option POSITION: disable an extra-select value
      // when no variant EXISTS for the running prefix + that value, and snap a
      // select off a now-invalid value. Color chips keep their Liquid-computed
      // global availability.
      var parts = [];
      for (var pos = 1; pos <= optionCount; pos++) {
        if (pos === colorPos) { parts.push(selectedColorIdx()); continue; }
        var sel = selectAtPosition(pos);
        if (!sel) { parts.push('0'); continue; }
        Array.prototype.forEach.call(sel.options, function (opt) {
          if (!opt.value && opt.disabled) return; // placeholder stays disabled
          opt.disabled = !existsWithPrefix(parts.concat([opt.getAttribute('data-idx') || '0']));
        });
        if (sel.options[sel.selectedIndex] && sel.options[sel.selectedIndex].disabled && sel.value) {
          for (var i = 0; i < sel.options.length; i++) {
            if (!sel.options[i].disabled && sel.options[i].value) { sel.selectedIndex = i; break; }
          }
        }
        var chosen = sel.options[sel.selectedIndex];
        parts.push(chosen ? (chosen.getAttribute('data-idx') || '0') : '0');
      }
    };
    var resolveColorVariant = function () {
      var syncChips = function () {
        colorRadios.forEach(function (x) {
          var lbl = panel.querySelector('label[for="' + x.id + '"]');
          if (lbl) lbl.classList.toggle('is-selected', x.checked);
        });
      };
      // While a required axis (e.g. "Select Seat Type") is unchosen, don't
      // resolve — submit stays blocked by the required-axis check.
      var pendingAxis = panel.querySelector('select[data-required-choice="true"]');
      if (pendingAxis && !pendingAxis.value) { syncChips(); return; }
      updateAvailability();
      var parts = [];
      for (var pos = 1; pos <= optionCount; pos++) {
        if (pos === colorPos) { parts.push(selectedColorIdx()); continue; }
        var sel = selectAtPosition(pos);
        var opt = sel ? sel.options[sel.selectedIndex] : null;
        parts.push(opt ? (opt.getAttribute('data-idx') || '0') : '0');
      }
      while (parts.length < 3) parts.push('0');
      var v = indexMap[parts.join('|')];
      syncChips();
      // Unresolvable combination → null variant, disabled ATC + inline message
      // (the old engine silently kept the previous variant/price here).
      setVariant(v ? v.id : null);
    };
    colorRadios.forEach(function (r) { r.addEventListener('change', resolveColorVariant); });
    extraSelects.forEach(function (s) { s.addEventListener('change', resolveColorVariant); });
    resolveColorVariant();
  }

  /* ---- addon controls → recompute ---- */
  selects.forEach(function (sel) { sel.addEventListener('change', recompute); });
  // 2026-10-01: line-item selectors (Seat Size …) carry no price, so nothing recomputed when one changed — the
  // required-choice status, the phone bar and the saved configuration all stayed one step behind.
  Array.prototype.forEach.call(panel.querySelectorAll('[data-lineitem-select]'), function (el) { el.addEventListener('change', recompute); });
  panel.querySelectorAll('[data-addon-radio]').forEach(function (r) { r.addEventListener('change', recompute); });
  checkboxes.forEach(function (b) {
    b.addEventListener('click', function () {
      if (b.disabled) return;
      var pressed = b.getAttribute('aria-pressed') === 'true';
      b.setAttribute('aria-pressed', pressed ? 'false' : 'true');
      b.textContent = pressed ? 'Add' : '✓ Added';   // 2026-09-27 revamp: words, not glyphs
      var card = b.closest('[data-accessory-card]');
      if (card) card.classList.toggle('is-selected', !pressed);
      recompute();
    });
  });
  /* ---- shared rear mount (post-launch 2026-09-29) ----
   * Add-on controls marked data-mount="rear" (snippets/ame-product-addons.liquid: Pride's one-rear-accessory rule)
   * compete for ONE bracket across groups and control types. Choosing one deselects any other, recomputes (so the
   * summary, cart lines and total follow) and says what was removed. A UI rule only — /cart/add.js, cart links and
   * checkout do not enforce it. */
  (function () {
    var rearEls = function () { return Array.prototype.slice.call(panel.querySelectorAll('[data-mount="rear"]')); };
    if (!rearEls().length) return;
    var titleOf = function (el) { return el.getAttribute('data-title') || (el.textContent || '').trim(); };
    var isOn = function (el) {
      if (el.tagName === 'OPTION') return el.selected && el.parentNode && el.parentNode.value === el.value;
      if (el.matches('[data-addon-checkbox]')) return el.getAttribute('aria-pressed') === 'true';
      return !!el.checked;
    };
    var turnOff = function (el) {
      if (el.tagName === 'OPTION') {
        var sel = el.parentNode; var none = sel.querySelector('option[value="0"]') || sel.querySelector('option[value=""]');
        if (none) sel.value = none.value;
      } else if (el.matches('[data-addon-checkbox]')) {
        el.setAttribute('aria-pressed', 'false'); el.textContent = 'Add';
        var card = el.closest('[data-accessory-card]'); if (card) card.classList.remove('is-selected');
      } else {
        var zero = panel.querySelector('input[type="radio"][name="' + el.name + '"][value="0"]');
        if (zero) zero.checked = true; else el.checked = false;
      }
    };
    var enforce = function (chosen) {
      var removed = [];
      rearEls().forEach(function (el) { if (el !== chosen && isOn(el)) { turnOff(el); removed.push(titleOf(el)); } });
      var note = (chosen.closest('[data-product-addons]') || panel).querySelector('[data-mount-note]') || panel.querySelector('[data-mount-note]');
      if (removed.length) {
        recompute();
        if (note) {
          note.textContent = 'Removed ' + removed.join(' and ') + ': Pride allows one rear-mounted accessory at a time, so ' +
            titleOf(chosen) + ' replaced it.';
          note.hidden = false;
        }
      } else if (note) { note.hidden = true; note.textContent = ''; }
    };
    // Any add-on interaction first clears the previous "Removed …" note (capture phase runs before the control's own
    // handler); enforce() shows it again only when this interaction actually removed something.
    var clearNotes = function () { panel.querySelectorAll('[data-mount-note]').forEach(function (n) { n.hidden = true; n.textContent = ''; }); };
    panel.addEventListener('change', function (e) { if (e.target.closest('[data-product-addons]')) clearNotes(); }, true);
    panel.addEventListener('click', function (e) { if (e.target.closest('[data-addon-checkbox]')) clearNotes(); }, true);
    rearEls().forEach(function (el) {
      if (el.tagName === 'OPTION') return;
      if (el.matches('[data-addon-checkbox]')) {
        el.addEventListener('click', function () { if (el.getAttribute('aria-pressed') === 'true') enforce(el); });
      } else {
        el.addEventListener('change', function () { if (el.checked) enforce(el); });
      }
    });
    selects.forEach(function (sel) {
      sel.addEventListener('change', function () {
        var opt = sel.options[sel.selectedIndex];
        if (opt && opt.getAttribute('data-mount') === 'rear') enforce(opt);
      });
    });
  })();

  /* ---- child availability (site validation 2026-10-02) ----
   * An add-on is rendered from the host's metafield JSON (variant id, price) — it does not know whether the
   * child can be bought today, and the cart add below is ATOMIC: one sold-out child makes Shopify refuse the whole
   * configuration (422, nothing added). Live example: the rear basket / crutch holders / back pack on 28 Pride, Jazzy
   * and CarryOn hosts are on a stock hold, so choosing one made the scooter itself impossible to add until the shopper
   * worked out which option to drop. Two guards, both fail-OPEN (a network error changes nothing; the cart stays the
   * only authority):
   *   1. a chosen child is checked once against the storefront's variant JSON (/variants/<id>.js); one that is not
   *      available is deselected and disabled, with a note on its row, before the shopper reaches Add to Cart;
   *   2. if /cart/add.js still answers 422, every chosen child is checked the same way, the unavailable ones are
   *      removed, and the message says so and invites a second Add to Cart. */
  var FRIENDLY_CART_ERROR = 'We couldn’t add this to your cart just now. Please try again, or call (803) 926-1493 and we’ll place the order for you.';
  var childAvail = (function () {
    var cache = {};
    // The storefront answers /variants/<id>.js for any published variant ({available, price, …}); the metafield
    // entry's handle is not needed (293 of 1,172 entries have none). A failed request is not cached, so a later check
    // (guard 2) asks again.
    function variantJson(vid) {
      if (!cache[vid]) {
        cache[vid] = fetch('/variants/' + vid + '.js', { credentials: 'same-origin', headers: { 'Accept': 'application/json' } })
          .then(function (r) { return r.ok ? r.json() : null; })
          .catch(function () { return null; })
          .then(function (v) { if (!v) delete cache[vid]; return v; });
      }
      return cache[vid];
    }
    // -> Promise<true | false | null>   (null = unknown: nothing changes)
    function available(el) {
      var vid = el.getAttribute('data-variant-id') || el.value;
      if (!vid || !/^\d+$/.test(vid)) return Promise.resolve(null);
      return variantJson(vid).then(function (v) { return v && typeof v.available === 'boolean' ? v.available : null; });
    }
    function isOn(el) {
      if (el.tagName === 'OPTION') return !!el.parentNode && el.parentNode.value === el.value;
      if (el.matches('[data-addon-checkbox]')) return el.getAttribute('aria-pressed') === 'true';
      return !!el.checked;
    }
    function fire(el) { el.dispatchEvent(new Event('change', { bubbles: true })); }
    // Deselect + disable through the native controls, so every rule above (rear mount, summary, total, saved
    // configuration) runs again. Returns the title when it had been selected, else false.
    function markUnavailable(el) {
      if (el.getAttribute('data-unavailable') === 'true') return false;
      var wasOn = isOn(el), title = el.getAttribute('data-title') || (el.textContent || '').trim();
      el.setAttribute('data-unavailable', 'true');
      if (el.tagName === 'OPTION') {
        var sel = el.parentNode;
        if (wasOn) { var none = sel.querySelector('option[value="0"]') || sel.querySelector('option[value=""]'); sel.value = none ? none.value : ''; }
        el.disabled = true;
        el.textContent = (el.getAttribute('data-title') || el.textContent).trim() + ' — currently unavailable';
        if (wasOn) fire(sel);
      } else if (el.matches('[data-addon-checkbox]')) {
        if (wasOn) el.click();            // the toggle handler turns it off, updates the card and recomputes
        el.disabled = true; el.textContent = 'Unavailable';
        var card = el.closest('[data-accessory-card]'); if (card) card.classList.add('is-unavailable');
      } else {
        if (wasOn) {
          var zero = panel.querySelector('input[type="radio"][name="' + el.name + '"][value="0"]');
          if (zero) { zero.checked = true; fire(zero); } else { el.checked = false; fire(el); }
        }
        el.disabled = true;
        var lab = el.closest('label') || panel.querySelector('label[for="' + el.id + '"]');
        if (lab) {
          lab.classList.add('is-unavailable');
          var price = lab.querySelector('.ame-customize__radio-price, .ame-customize__card-price');
          if (price) price.textContent = 'Currently unavailable';
        }
      }
      return wasOn ? title : false;
    }
    function verify(el) { return available(el).then(function (ok) { return ok === false ? markUnavailable(el) : false; }); }
    function rowOf(el) { return el.closest('[data-accessory-card], .ame-customize__row, [data-option-row]') || (el.tagName === 'OPTION' ? el.parentNode.closest('.ame-customize__row') : null); }
    function note(el, title) {
      if (!title) return;
      var row = rowOf(el) || panel;
      var n = document.createElement('p');
      n.className = 'ame-customize__inline-error ame-customize__inline-error--notice'; n.setAttribute('role', 'status'); n.setAttribute('data-availability-note', '');
      n.textContent = '“' + title + '” is currently unavailable, so it was removed from your configuration. Everything else is kept.';
      row.insertBefore(n, row.firstChild);
    }
    function chosenEls() {
      var els = [];
      selects.forEach(function (sel) { var o = sel.options[sel.selectedIndex]; if (o && /^\d+$/.test(o.value)) els.push(o); });
      checkboxes.forEach(function (b) { if (b.getAttribute('aria-pressed') === 'true' && b.getAttribute('data-variant-id')) els.push(b); });
      Array.prototype.forEach.call(panel.querySelectorAll('[data-addon-radio]'), function (r) { if (r.checked && r.getAttribute('data-variant-id')) els.push(r); });
      return els;
    }
    // -> Promise<[titles removed]>
    function sweep() { return Promise.all(chosenEls().map(verify)).then(function (r) { return r.filter(Boolean); }); }
    // guard 1 — the control's own handlers have long run by the time the fetch settles
    panel.addEventListener('change', function (e) {
      var t = e.target; if (!t || !t.matches) return;
      if (t.matches('select[data-addon-select]')) { var o = t.options[t.selectedIndex]; if (o && /^\d+$/.test(o.value)) verify(o).then(function (ti) { note(o, ti); }); }
      else if (t.matches('[data-addon-radio]') && t.checked && t.getAttribute('data-variant-id')) verify(t).then(function (ti) { note(t, ti); });
    });
    panel.addEventListener('click', function (e) {
      var b = e.target && e.target.closest && e.target.closest('[data-addon-checkbox][data-variant-id]');
      if (b) setTimeout(function () { if (b.getAttribute('aria-pressed') === 'true') verify(b).then(function (ti) { note(b, ti); }); }, 0);
    });
    return { sweep: sweep };
  })();

  // default_checked cards must show selected styling on first paint.
  checkboxes.forEach(function (b) {
    if (b.getAttribute('aria-pressed') === 'true') {
      var c = b.closest('[data-accessory-card]');
      if (c) c.classList.add('is-selected');
    }
  });

  /* ---- quantity ---- */
  if (qtyInput) {
    var syncQty = function () {
      var q = parseInt(qtyInput.value, 10);
      if (!q || q < 1) { q = 1; qtyInput.value = '1'; }
      state.quantity = q;
      recompute();
    };
    qtyInput.addEventListener('change', syncQty);
    qtyInput.addEventListener('input', syncQty);
  }

  /* ---- fabric picker ---- */
  if (fabricPicker && fabricTrigger && fabricPanel) {
    var openPicker = function () { fabricPanel.hidden = false; fabricTrigger.setAttribute('aria-expanded', 'true'); };
    var closePicker = function () { fabricPanel.hidden = true; fabricTrigger.setAttribute('aria-expanded', 'false'); };
    fabricTrigger.addEventListener('click', function (e) {
      e.stopPropagation();
      if (fabricPanel.hidden) openPicker(); else closePicker();
    });
    document.addEventListener('click', function (e) {
      if (!fabricPanel.hidden && !fabricPicker.contains(e.target)) closePicker();
    });
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !fabricPanel.hidden) closePicker();
    });
    fabricPicker.querySelectorAll('[data-fabric-option]').forEach(function (opt) {
      opt.addEventListener('click', function () {
        // Non-chargeable upgrade fabrics (no matching upgrade variant) cannot be
        // selected — displayed total must always equal what the cart charges.
        if (opt.getAttribute('data-fabric-chargeable') === 'false') return;
        if (optionExcludedForCurrentSize(opt)) return; // size rule (custom.option_size_exclusions)
        var name = opt.getAttribute('data-fabric-name') || '';
        var coll = opt.getAttribute('data-fabric-collection') || '';
        // Base price/variant come from the stable base attrs; the visible
        // upcharge attr may already hold a size-effective value (see
        // applyFabricSizeOverrides). Size-priced fabrics carry a
        // data-fabric-variant-overrides map of {sizeTitle: {id, cents}}.
        var upCents = parseInt(opt.getAttribute('data-fabric-base-cents') || opt.getAttribute('data-fabric-upcharge-cents') || opt.getAttribute('data-fabric-upcharge') || '0', 10) || 0;
        var variantId = opt.getAttribute('data-fabric-variant-id') || '';
        var swatch = opt.getAttribute('data-fabric-swatch') || '';
        var sizeOverrides = {};
        try { sizeOverrides = JSON.parse(opt.getAttribute('data-fabric-variant-overrides') || '{}') || {}; }
        catch (_) { sizeOverrides = {}; }
        if (fabricTrigger) fabricTrigger.removeAttribute('aria-invalid');
        state.fabric = { name: name, collection: coll, upchargeCents: upCents, variantId: variantId,
                         baseUpchargeCents: upCents, baseVariantId: variantId, sizeOverrides: sizeOverrides,
                         chargeable: upCents === 0 || !!variantId };
        if (fabricNameEl)       fabricNameEl.value = name;
        if (fabricCollectionEl) fabricCollectionEl.value = coll;
        if (fabricUpchargeEl)   fabricUpchargeEl.value = String(upCents);
        if (fabricVariantIdEl)  fabricVariantIdEl.value = variantId;
        if (fabricLabel) fabricLabel.textContent = name + (coll && coll !== 'Standard' ? ' — ' + coll : '');
        if (fabricSwatchPrev) fabricSwatchPrev.style.backgroundImage = swatch ? "url('" + swatch + "')" : '';
        fabricPicker.querySelectorAll('[aria-selected="true"]').forEach(function (el) { el.setAttribute('aria-selected', 'false'); });
        opt.setAttribute('aria-selected', 'true');
        closePicker();
        if (typeof applyFabricSizeOverrides === 'function') applyFabricSizeOverrides();
        recompute();
      });
    });
  }

  /* ---- heel-loop ↔ footrest compatibility (unchanged rules) ---- */
  panel.querySelectorAll('[data-addon-compat-group]').forEach(function (dd) {
    var groupKey = dd.getAttribute('data-addon-compat-group');
    var footRadios = Array.prototype.slice.call(
      panel.querySelectorAll('[data-addon-radio][data-group-key="' + groupKey + '"]'));
    var footSelect = panel.querySelector('select[data-addon-select][data-addon-key="' + groupKey + '"]');
    if (!footRadios.length && !footSelect) return;
    var footrestClass = function () {
      var title = '';
      if (footSelect) {
        var o = footSelect.options[footSelect.selectedIndex];
        title = o ? (o.getAttribute('data-title') || o.textContent || '') : '';
      } else {
        var r = footRadios.filter(function (x) { return x.checked; })[0];
        title = r ? (r.getAttribute('data-title') || '') : '';
      }
      if (/elevating/i.test(title)) return 'elevating';
      if (/swing[- ]?away/i.test(title)) return 'swingaway';
      return 'standard';
    };
    /* ★★ RECONCILE, DON'T SILENTLY DROP (2026-09-27, Tom's EVO 613 Li / 614HD report). The old rule disabled the
       heel loops that don't fit the chosen leg rest and, when the shopper changed leg rests after picking loops,
       reset the pick to "No Heel Loops" with no message — the shopper saw their heel loops vanish, or a greyed
       "Elevating" option they could not choose, and read it as a bug. Now: the compatible loops are enabled, an
       incompatible pick is swapped to the matching loops for the new leg rest when exactly one exists (each heel
       loop record is leg-rest-specific), otherwise cleared, and a hint under the dropdown says what happened and
       which loops fit. The submit guard below makes the DOM state irrelevant to what reaches the cart. */
    var hint = dd.parentElement ? dd.parentElement.querySelector('[data-compat-hint]') : null;
    var footTitle = function () {
      if (footSelect) { var o = footSelect.options[footSelect.selectedIndex]; return o ? (o.getAttribute('data-title') || o.textContent || '').trim() : ''; }
      var r = footRadios.filter(function (x) { return x.checked; })[0]; return r ? (r.getAttribute('data-title') || '') : '';
    };
    var setHint = function (msg) { if (!hint) return; hint.textContent = msg || ''; hint.hidden = !msg; };
    var applyCompat = function () {
      var cls = footrestClass();
      var compatible = [], picked = null;
      Array.prototype.forEach.call(dd.options, function (opt) {
        var compat = opt.getAttribute('data-compat');
        if (!compat) { opt.disabled = false; return; }
        var ok = compat.split(',').indexOf(cls) !== -1;
        opt.disabled = !ok;
        if (ok) compatible.push(opt);
        if (opt.selected) picked = ok ? null : opt;
      });
      var groupLabel = (dd.getAttribute('data-group-label') || 'Heel loops').toLowerCase();
      var legLabel = footTitle().replace(/\s*\(pair\)\s*$/i, '');
      if (picked) {
        var was = (picked.getAttribute('data-title') || picked.textContent || '').trim();
        if (compatible.length === 1) {
          dd.value = compatible[0].value;
          setHint('Switched ' + groupLabel + ' to the ones that fit ' + legLabel + '.');
        } else {
          dd.value = '0';
          setHint(was + ' do not fit ' + legLabel + ' — ' + groupLabel + ' cleared. Choose the loops listed for this leg rest if you want them.');
        }
        dd.dispatchEvent(new Event('change', { bubbles: true }));
      } else if (cls === 'elevating' || cls === 'swingaway') {
        setHint('Showing the ' + groupLabel + ' made for ' + legLabel + '.');
      } else {
        setHint('');
      }
    };
    var compatOk = function () {
      var opt = dd.options[dd.selectedIndex]; var compat = opt && opt.getAttribute('data-compat');
      return !compat || compat.split(',').indexOf(footrestClass()) !== -1;
    };
    dd.setAttribute('data-compat-check', '');
    dd._ameCompatOk = compatOk;
    footRadios.forEach(function (r) { r.addEventListener('change', applyCompat); });
    if (footSelect) footSelect.addEventListener('change', applyCompat);
    applyCompat();
  });

  /* ---- option-dependent accessories (T2 2026-09-19) ----------------------------------------------
     An accessory card may declare the variant option position it depends on and the value(s) it fits
     (data-option-compat-position / data-option-compat-values, pipe-joined). When the shopper picks an
     option value the card does not fit — e.g. the 20x18 compact seat against the Style C cup holder —
     the card is disabled, deselected and explained, so an incompatible pairing cannot reach the cart. */
  var compatCards = Array.prototype.slice.call(panel.querySelectorAll('[data-accessory-card][data-option-compat-values]'));
  if (compatCards.length) {
    var selectedOptionValue = function (pos) {
      var r = panel.querySelector('[data-axis-cards][data-option-position="' + pos + '"] [data-axis-radio]:checked');
      if (r) return r.value;
      var sel = panel.querySelector('[data-extra-option][data-option-position="' + pos + '"]');
      if (sel && sel.selectedIndex >= 0) return (sel.options[sel.selectedIndex].getAttribute('data-title') || sel.options[sel.selectedIndex].textContent || '').trim();
      return null;
    };
    var applyOptionCompat = function () {
      var changed = false;
      compatCards.forEach(function (card) {
        var pos = card.getAttribute('data-option-compat-position');
        var allowed = (card.getAttribute('data-option-compat-values') || '').split('|').map(function (x) { return x.trim(); });
        var current = selectedOptionValue(pos);
        var ok = !current || allowed.indexOf(current) !== -1;
        var btn = card.querySelector('[data-addon-checkbox]');
        card.classList.toggle('is-incompatible', !ok);
        card.setAttribute('aria-disabled', ok ? 'false' : 'true');
        var note = card.querySelector('[data-compat-note]');
        if (!ok && !note) {
          note = document.createElement('span');
          note.className = 'ame-customize__hint';
          note.setAttribute('data-compat-note', '');
          note.textContent = 'Fits the ' + allowed.join(' or ') + ' only.';
          (card.querySelector('.ame-customize__accessory-info') || card).appendChild(note);
        } else if (ok && note) { note.remove(); }
        if (btn) {
          btn.disabled = !ok;
          if (!ok && btn.getAttribute('aria-pressed') === 'true') {
            // deselect completely: state AND the visible selected styling/label (the card used to stay "✓ selected")
            btn.setAttribute('aria-pressed', 'false'); btn.textContent = 'Add'; card.classList.remove('is-selected'); changed = true;
          }
        }
      });
      if (changed && typeof recompute === 'function') recompute();
    };
    panel.querySelectorAll('[data-axis-radio], [data-extra-option], [data-color-radio]').forEach(function (el) {
      el.addEventListener('change', applyOptionCompat);
    });
    applyOptionCompat();
  }

  /* ---- size-conditional footrest (custom.footrest_eligible_sizes) ---- */
  var footrestRow = panel.querySelector('[data-footrest-row]');
  var footrestSelect = footrestRow ? footrestRow.querySelector('[data-addon-key="footrest"]') : null;
  var footrestSizes = [];
  try { footrestSizes = JSON.parse(footrestRow ? footrestRow.getAttribute('data-footrest-eligible-sizes-json') || '[]' : '[]'); }
  catch (_) { footrestSizes = []; }
  if (!footrestSizes.length && footrestRow && footrestRow.hasAttribute('data-footrest-eligible-sizes')) {
    // Legacy pipe-joined attribute — kept as fallback during rollout.
    footrestSizes = (footrestRow.getAttribute('data-footrest-eligible-sizes') || '')
      .split('|').map(function (s) { return s.trim(); }).filter(Boolean);
  }
  var currentSizeTitle = function () {
    // Works for both the <select> axis and the variant-card radios.
    if (variantSelect && variantSelect.tagName === 'SELECT') {
      var opt = variantSelect.options[variantSelect.selectedIndex];
      return opt ? (opt.getAttribute('data-size-title') || '') : '';
    }
    var checkedCard = panel.querySelector('[data-variant-card-radio]:checked');
    return checkedCard ? (checkedCard.getAttribute('data-size-title') || '') : '';
  };
  var hasSizeSource = variantSelect && (variantSelect.tagName === 'SELECT' || panel.querySelector('[data-variant-card-radio]'));
  if (footrestRow && footrestSelect && footrestSizes.length && hasSizeSource) {
    var syncFootrestEligibility = function () {
      var ok = footrestSizes.indexOf(currentSizeTitle()) !== -1;
      footrestRow.hidden = !ok;
      footrestRow.style.display = ok ? '' : 'none';
      if (!ok && footrestSelect.value !== '0') {
        footrestSelect.value = '0';
        footrestSelect.dispatchEvent(new Event('change', { bubbles: true }));
      }
    };
    if (variantSelect.tagName === 'SELECT') {
      variantSelect.addEventListener('change', syncFootrestEligibility);
    } else {
      panel.querySelectorAll('[data-variant-card-radio]').forEach(function (r) {
        r.addEventListener('change', syncFootrestEligibility);
      });
    }
    syncFootrestEligibility();
  }

  /* ---- size-aware fabric upgrade pricing ----
     Declarative counterpart of custom.fabric_upgrade_overrides: picker options
     for size-priced fabrics carry data-fabric-variant-overrides
     ({sizeTitle: {id, cents}}, emitted by ame-product-customize.liquid from
     the override product's variants). On load and on every ame:variant-change
     this re-resolves the EFFECTIVE charging variant + price for the current
     size — the commerce truth stays a real Shopify variant, never front-end
     arithmetic. Products without overrides take the base path unchanged. */
  var applyFabricSizeOverrides = function () {
    var size = currentSizeTitle();
    var groupCents = {};   // collection -> effective cents of its first paid option
    var effByName = {};    // fabric name -> effective cents (for the swatch-browser copies)
    panel.querySelectorAll('[data-fabric-option]').forEach(function (el) {
      var base = parseInt(el.getAttribute('data-fabric-base-cents') || el.getAttribute('data-fabric-upcharge-cents') || '0', 10) || 0;
      if (!el.hasAttribute('data-fabric-base-cents')) el.setAttribute('data-fabric-base-cents', String(base));
      var cents = base;
      var raw = el.getAttribute('data-fabric-variant-overrides');
      if (raw && size) {
        try {
          var o = JSON.parse(raw);
          if (o && o[size] && typeof o[size].cents === 'number') cents = o[size].cents;
        } catch (_) { /* malformed map -> base price */ }
      }
      el.setAttribute('data-fabric-upcharge-cents', String(cents));
      var nm = el.getAttribute('data-fabric-name') || '';
      if (nm) effByName[nm] = cents;
      var collName = el.getAttribute('data-fabric-collection') || '';
      if (cents > 0 && collName && !(collName in groupCents)) groupCents[collName] = cents;
      var priceEl = el.querySelector('.ame-fabric-picker__option-price');
      if (priceEl && priceEl.textContent.indexOf('Call') === -1) priceEl.textContent = cents > 0 ? '+$' + Math.round(cents / 100) : 'Included';   // $0 is "Included", never "+$0"
    });
    // Swatch-browser copies of the same fabrics (lightbox reads the attr).
    document.querySelectorAll('.ame-fabric-swatches__btn[data-fabric-name]').forEach(function (cell) {
      var nm = cell.getAttribute('data-fabric-name');
      if (nm in effByName) cell.setAttribute('data-fabric-upcharge-cents', String(effByName[nm]));
    });
    // Collection badges on both surfaces ("+$286 upgrade" / "+$286").
    document.querySelectorAll('[data-fabric-group-upcharge]').forEach(function (span) {
      var collName = span.getAttribute('data-fabric-group-upcharge');
      var cents = groupCents[collName];
      if (typeof cents !== 'number') cents = parseInt(span.getAttribute('data-fabric-group-base-cents') || '0', 10) || 0;
      if (!cents) return;
      var suffix = span.classList.contains('ame-fabric-picker__group-meta') ? ' upgrade' : '';
      span.textContent = '+$' + Math.round(cents / 100) + suffix;
    });
    // Re-resolve the selected fabric's charging variant + price.
    if (state.fabric && state.fabric.chargeable) {
      var eff = { id: state.fabric.baseVariantId || state.fabric.variantId || '',
                  cents: (typeof state.fabric.baseUpchargeCents === 'number') ? state.fabric.baseUpchargeCents : state.fabric.upchargeCents };
      var o2 = state.fabric.sizeOverrides || {};
      if (size && o2[size] && o2[size].id && eff.id) eff = { id: String(o2[size].id), cents: o2[size].cents };
      state.fabric.variantId = eff.id;
      state.fabric.upchargeCents = eff.cents;
      if (fabricUpchargeEl) fabricUpchargeEl.value = String(eff.cents);
      if (fabricVariantIdEl) fabricVariantIdEl.value = eff.id;
      recompute();
    }
  };
  document.addEventListener('ame:variant-change', applyFabricSizeOverrides);
  applyFabricSizeOverrides();

  /* ---- model configuration capacity notice ---- */
  var mcSelect = panel.querySelector('[data-addon-key="model_config"]');
  var mcNote = panel.querySelector('[data-model-config-note]');
  if (mcSelect && mcNote) {
    var syncModelConfigNote = function () {
      var isDeluxe = mcSelect.value && mcSelect.value !== '0';
      var note = mcSelect.getAttribute('data-capacity-note') || '';
      var height = mcSelect.getAttribute('data-seat-height') || '';
      if (isDeluxe && note) {
        mcNote.textContent = '';
        mcNote.appendChild(document.createTextNode('⚠ ' + note));
        if (height) {
          mcNote.appendChild(document.createElement('br'));
          mcNote.appendChild(document.createTextNode('Seat height range: ' + height + '.'));
        }
        mcNote.hidden = false;
      } else {
        mcNote.hidden = true;
        mcNote.textContent = '';
      }
    };
    mcSelect.addEventListener('change', syncModelConfigNote);
    syncModelConfigNote();
  }

  /* ---- jump to the fabric browser ---- */
  // pdp-v2 renders fabrics as an Overview block (#ame-pdp-fabrics); legacy
  // templates keep their Fabric Collections tab — one handler serves both.
  document.querySelectorAll('[data-jump-to-fabric]').forEach(function (link) {
    link.addEventListener('click', function (e) {
      e.preventDefault();
      var block = document.getElementById('ame-pdp-fabrics');
      if (block) {
        // PDP v3: the fabric block lives in the Product Details accordion — open it first
        if (window.AmeAcc) window.AmeAcc.reveal(block);
        block.scrollIntoView({ behavior: 'smooth', block: 'start' });
        return;
      }
      var fabricTab = document.getElementById('tab-btn-fabric');
      if (fabricTab) {
        fabricTab.click();
        fabricTab.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    });
  });

  /* ---- jump-to-tab links ----
     PDP v3 (2026-09-26): the tab row became vertical accordions; [data-jump-to-tab] links are handled once, by
     the delegated listener in assets/ame-product-tabs.js (opens the section, then scrolls). */

  /* ---- submit ---- */
  function showSubmitError(msg, focusEl) {
    track('customizer_validation_error', { error_type: focusEl ? 'required_missing' : 'cart_error', option_group: focusEl && window.ameTrackGroup ? window.ameTrackGroup(focusEl) : '' });
    if (errorsEl) {
      errorsEl.hidden = false;
      errorsEl.textContent = msg;
      errorsEl.scrollIntoView({ behavior: smooth(), block: 'center' });
    }
    // Accessory groups render inside a collapsed <details>. Without this, an
    // error can name a control the shopper cannot see and .focus() silently
    // does nothing — reachable today via the Avenger's required battery pair.
    if (focusEl && focusEl.closest) {
      var det = focusEl.closest('details');
      while (det) {
        det.open = true;
        det = det.parentElement && det.parentElement.closest('details');
      }
    }
    // V2 R3 (2026-09-27): the summary message sits by Add to Cart, but the page then scrolls to the field, so the
    // shopper saw the field with no explanation. Repeat the message inline at the top of that field's row and tie it
    // to the focused control; it clears on the next change anywhere in the panel. Display only — no cart logic.
    if (focusEl && focusEl.closest) {
      var row = focusEl.closest('[data-option-row], .ame-customize__row, .ame-customize__accessories, .ame-fabric-picker') || focusEl.parentElement;
      if (row) {
        var inline = row.querySelector(':scope > .ame-customize__inline-error');
        if (!inline) {
          inline = document.createElement('p');
          inline.className = 'ame-customize__inline-error';
          inline.id = 'ame-inline-err-' + Math.random().toString(36).slice(2, 8);
          row.insertBefore(inline, row.firstChild);
        }
        inline.textContent = msg;
        row.classList.add('is-invalid');
        var target = focusEl;
        var proxy = row.querySelector('.ui-optcard');   // the option-card proxy UI takes focus instead of the hidden select
        [target, proxy].forEach(function (el) { if (el && el.setAttribute) el.setAttribute('aria-describedby', inline.id); });
      }
    }
    if (focusEl && focusEl.focus) focusEl.focus();
    if (focusEl && focusEl.scrollIntoView) {
      focusEl.scrollIntoView({ behavior: smooth(), block: 'center' });
    }
  }
  panel.addEventListener('change', function () {
    Array.prototype.forEach.call(panel.querySelectorAll('.ame-customize__inline-error'), function (e) {
      var r = e.parentElement; if (r) r.classList.remove('is-invalid');
      Array.prototype.forEach.call(document.querySelectorAll('[aria-describedby="' + e.id + '"]'), function (x) { x.removeAttribute('aria-describedby'); });
      e.remove();
    });
  });

  if (form) {
    // Hard guard: never submit the footrest line on an ineligible size (capture
    // phase → runs before the main handler reads picks).
    if (footrestSelect && footrestSizes.length) {
      form.addEventListener('submit', function () {
        if (footrestSizes.indexOf(currentSizeTitle()) === -1) footrestSelect.value = '0';
      }, true);
    }
    // Hard guard for size-excluded options (custom.option_size_exclusions): strip any excluded
    // pick before the main handler reads the controls, whatever state the DOM was put in.
    form.addEventListener('submit', function () {
      selects.forEach(function (sel) {
        var opt = sel.options[sel.selectedIndex];
        if (opt && sel.value !== '0' && optionExcludedForCurrentSize(opt)) sel.value = '0';
        // Hard guard for leg-rest ↔ heel-loop compatibility: whatever the DOM was put in (an old iOS picker
        // ignoring `disabled`, devtools), loops that do not fit the chosen leg rest never reach the cart.
        if (sel._ameCompatOk && sel.value !== '0' && !sel._ameCompatOk()) sel.value = '0';
      });
      var fn = (state.fabric && state.fabric.name) || (fabricNameEl && fabricNameEl.value) || '';
      if (fn && fabricExcludedForCurrentSize(fn)) clearFabricSelection();
    }, true);

    form.addEventListener('submit', function (e) {
      if (errorsEl) { errorsEl.hidden = true; errorsEl.textContent = ''; }

      // Required product_addons must be selected — runs before any early return.
      var reqAddons = Array.prototype.slice.call(panel.querySelectorAll('[data-addon-required="true"]'));
      for (var rq = 0; rq < reqAddons.length; rq++) {
        if (reqAddons[rq].getAttribute('aria-pressed') !== 'true') {
          e.preventDefault();
          showSubmitError('Please choose ' + (reqAddons[rq].getAttribute('data-title') || 'all required options') + ' before adding to cart.', reqAddons[rq]);
          return;
        }
      }

      var picks = selectedAddOns();
      var fabricName = (fabricNameEl && fabricNameEl.value) || '';
      var fabricVariantId = (fabricVariantIdEl && fabricVariantIdEl.value) || '';

      // Upholstery is a required decision wherever a fabric picker renders: an
      // order without an Upholstery property gives fulfillment no fabric to
      // build, and no manufacturer default exists to fall back on — so block
      // the submit instead of silently carting a fabric-less chair.
      if (fabricPicker && !fabricName) {
        e.preventDefault();
        if (fabricTrigger) fabricTrigger.setAttribute('aria-invalid', 'true');
        showSubmitError('Select an upholstery option before adding this chair to your cart.', fabricTrigger);
        return;
      }
      var liSelects = Array.prototype.slice.call(panel.querySelectorAll('[data-lineitem-select]'));
      var hasModelConfig = !!panel.querySelector('[data-addon-key="model_config"]');

      // ★★★ 2026-10-01: BOTH required checks below now run BEFORE the EXPO early return. They used to sit after it,
      // so on an EXPO-managed product with nothing else picked the handler returned first and the browser posted
      // the bare variant natively: the BestLift PL350CT went into the cart WITHOUT its required Sling Size and
      // with no message (reproduced on the base theme and on staging before this change).
      // Required ADD-ON groups (e.g. the PL350CT sling size). Deliberately separate from the
      // required variant AXIS below: the lift's variant is already resolved, so this must gate the
      // submit only — reusing data-required-choice would also stall variant resolution.
      var reqAddon = Array.prototype.slice.call(
        panel.querySelectorAll('select[data-addon-required-group="true"]'));
      for (var rg = 0; rg < reqAddon.length; rg++) {
        if (!reqAddon[rg].value) {
          e.preventDefault();
          showSubmitError('Please choose ' +
            (reqAddon[rg].getAttribute('data-addon-required-label') || 'all required options') +
            ' before adding to cart.', reqAddon[rg]);
          return;
        }
      }
      // Required variant-axis choices (e.g. "Select Seat Type").
      var reqAxis = Array.prototype.slice.call(panel.querySelectorAll('select[data-required-choice="true"]'));
      for (var ra = 0; ra < reqAxis.length; ra++) {
        if (!reqAxis[ra].value) {
          e.preventDefault();
          // The label travels on the control. A fixed "Seat Type" is a claim about a product this
          // check does not know; the literal stays only as the fallback so existing pages are
          // byte-identical in behaviour.
          showSubmitError('Please choose ' +
            (reqAxis[ra].getAttribute('data-required-choice-label') || 'a Seat Type') +
            ' before adding to cart.', reqAxis[ra]);
          return;
        }
      }
      // ★★★ 2026-10-06: the EXPO native-post shortcut is gone. It used to let a product WITHOUT
      // app--…--sys.expo_migration_notes post the bare variant natively when nothing else was picked, so
      // EXPO's cart-attachment hook could fire. EXPO is no longer on the storefront (no app script on any
      // PDP), and 42 live hosts — the Jazzy Ultra Light, Jazzy Carbon HD/27/27X, Go-Go Elite Traveller,
      // Golden Stride/Ally, every BestLift/BestStand … — have service/delivery config but no migration note.
      // On those, a chair added with no accessory lost its Service Agreement, Delivery and _ame_config_id
      // (sal03 depth probe, live). Every add now takes the AJAX path below, like every configured add.

      // Required line-item selectors.
      for (var i = 0; i < liSelects.length; i++) {
        if (liSelects[i].getAttribute('data-lineitem-required') === 'true' && !liSelects[i].value) {
          e.preventDefault();
          showSubmitError('Please choose ' + (liSelects[i].getAttribute('data-lineitem-label') || 'all required options') + ' before adding to cart.', liSelects[i]);
          return;
        }
      }
      e.preventDefault();

      if (!state.derived.canAddToCart) { track('customizer_validation_error', { error_type: 'unavailable_combination', option_group: '' }); renderErrors(); return; }

      var btn = atcBtn;
      if (btn) { btn.dataset.adding = '1'; btn.disabled = true; btn.dataset._label = btn.textContent; btn.textContent = 'Adding...'; }
      var done = function () {
        if (btn) { delete btn.dataset.adding; btn.disabled = !state.derived.canAddToCart; btn.textContent = btn.dataset._label || 'Add to Cart'; }
      };

      var mainId = (form.querySelector('[name="id"]') || {}).value;
      var quantity = state.quantity;
      // One unique configuration id per submission — parent + every child line.
      var _ameConfigId = 'cfg-' + Date.now().toString(36) + Math.random().toString(36).slice(2, 8);

      var properties = {};
      if (fabricName) {
        var coll = (fabricCollectionEl && fabricCollectionEl.value) || '';
        var up = parseInt((fabricUpchargeEl && fabricUpchargeEl.value) || '0', 10) || 0;
        properties['Upholstery'] = fabricName + (coll ? ' — ' + coll : '') + (up > 0 ? ' (+$' + (up / 100).toLocaleString() + ' upgrade)' : '');
      }
      liSelects.forEach(function (s) { if (s.value) properties[s.getAttribute('data-lineitem-label') || 'Option'] = s.value; });
      // Property-only add-on choices (no variant behind them — e.g. the GB120 free-gift
      // bag) live on the parent line as "<group label>: <choice>". They are never posted
      // as their own line, so nothing $0 can be added on its own (see ame-product-addons).
      selects.forEach(function (s) {
        var o = s.options[s.selectedIndex];
        if (o && o.getAttribute('data-property-only') === 'true' && s.value && s.value.indexOf('prop:') === 0) {
          properties[s.getAttribute('data-group-label') || 'Option'] = o.getAttribute('data-title') || s.value.slice(5);
        }
      });
      // Included manufacturer service label as a $0 property on the parent line.
      var svcSel = panel.querySelector('[data-addon-key="service"]');
      if (svcSel && svcSel.getAttribute('data-service-included') && (!svcSel.value || svcSel.value === '0')) {
        properties['Service Agreement'] = svcSel.getAttribute('data-service-included');
      }
      // Model configuration descriptive properties on the parent line.
      var mcSel = panel.querySelector('[data-addon-key="model_config"]');
      if (mcSel) {
        if (mcSel.value && mcSel.value !== '0') {
          properties['Amigo Configuration'] = mcSel.getAttribute('data-deluxe-label') || 'Deluxe';
          if (mcSel.getAttribute('data-lift-label')) properties['Power Seat Lift'] = mcSel.getAttribute('data-lift-label');
          if (mcSel.getAttribute('data-deluxe-capacity')) properties['Configured Weight Capacity'] = mcSel.getAttribute('data-deluxe-capacity');
          if (mcSel.getAttribute('data-seat-height')) properties['Seat Height Range'] = mcSel.getAttribute('data-seat-height');
        } else {
          properties['Amigo Configuration'] = 'Standard';
          if (mcSel.getAttribute('data-std-capacity')) properties['Configured Weight Capacity'] = mcSel.getAttribute('data-std-capacity');
        }
      }
      // Delivery property, metafield-driven (replaces the old handle allowlists):
      // a rendered delivery select stamps the chosen tier (or the included
      // default); island.deliveryIncludedLabel stamps included curbside on
      // products with no delivery choices.
      var dsel = panel.querySelector('select[data-addon-key="delivery"]');
      if (dsel) {
        var dtitle = 'Free Curbside Delivery';
        if (dsel.value && dsel.value !== '0') {
          var dopt = dsel.options[dsel.selectedIndex];
          if (dopt) dtitle = dopt.getAttribute('data-title') || dtitle;
        }
        properties['Delivery'] = dtitle;
      } else if (island.deliveryIncludedLabel) {
        properties['Delivery'] = island.deliveryIncludedLabel;
      }
      properties['_ame_config_id'] = _ameConfigId;

      var chairLine = { id: mainId, quantity: quantity, properties: properties };
      var items = [chairLine];
      var addonProps = { '_Parent product handle': parentHandle, '_Added as accessory': 'true',
        '_ame_addon_type': 'addon', '_ame_parent_variant_id': String(mainId), '_ame_config_id': _ameConfigId };
      if (fabricVariantId) items.push({ id: fabricVariantId, quantity: quantity, properties: addonProps });
      picks.forEach(function (a) {
        var props = addonProps;
        if (a.key === 'service') {
          props = { '_Parent product handle': parentHandle, '_Added as accessory': 'true',
            '_ame_addon_type': 'pride_service', '_ame_parent_variant_id': String(mainId),
            '_ame_service_term': a.title, '_ame_config_id': _ameConfigId };
        } else if (a.key === 'model_config') {
          props = { '_Parent product handle': parentHandle, '_Added as accessory': 'true',
            '_ame_addon_type': 'model_config', '_ame_parent_variant_id': String(mainId),
            '_ame_service_term': a.title, '_ame_config_id': _ameConfigId };
        } else if (a.groupLabel) {
          // Lift-config style picks carry a public group property (e.g.
          // "Installation: Certified LiftSquad Install") for fulfillment.
          props = { '_Parent product handle': parentHandle, '_Added as accessory': 'true',
            '_ame_addon_type': 'addon', '_ame_parent_variant_id': String(mainId),
            '_ame_config_id': _ameConfigId };
          props[a.groupLabel] = a.title;
        }
        // Configuration children scale with the parent quantity; discrete
        // accessory checkboxes stay one per toggle.
        var lineQty = a.kind === 'checkbox' ? 1 : quantity;
        items.push({ id: a.variantId, quantity: lineQty, properties: props });
      });

      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({ items: items })
      })
        .then(function (r) {
          if (!r.ok) {
            return r.json().catch(function () { return null; }).then(function (d) {
              // 2026-10-02: not JSON (bot challenge page, 5xx) → nothing to interpret; say so in our own words
              if (!d || typeof d !== 'object') { var ne = new Error(FRIENDLY_CART_ERROR); ne.ameFriendly = true; throw ne; }
              // Atomic multi-line add: nothing was added. Map the 422 to the
              // offending line when the description names it.
              var desc = d.description || d.message || 'Add to cart failed';
              var hit = picks.filter(function (a) { return a.title && desc.indexOf(a.title) !== -1; })[0];
              var msg = hit
                ? 'We couldn’t add “' + hit.title + '” — ' + desc + ' Your configuration is unchanged.'
                : desc + ' Nothing was added to your cart — your configuration is unchanged.';
              // guard 2 (2026-10-02): a child that can no longer be bought is dropped here, not left for the
              // shopper to find by trial and error. Fail-open: nothing found → the message above.
              return childAvail.sweep().then(function (removed) {
                var e = new Error(removed.length
                  ? '“' + removed.join('” and “') + '” ' + (removed.length > 1 ? 'are' : 'is') +
                    ' currently unavailable, so we removed ' + (removed.length > 1 ? 'them' : 'it') +
                    ' from your configuration. Everything else is kept — press Add to Cart again to continue.'
                  : msg);
                e.ameFriendly = true; throw e;
              });
            });
          }
          track('customizer_complete', { configuration_complete: 'yes', line_count: items.length, selection_count: picks.length + (fabricName ? 1 : 0),
            has_service_plan: picks.some(function (a) { return a.key === 'service'; }) ? 'yes' : 'no',
            delivery_upgrade: picks.some(function (a) { return a.key === 'delivery'; }) ? 'yes' : 'no' });
          saveState(true);
          window.location.href = '/cart';
        })
        .catch(function (err) {
          done();
          // 2026-10-02: only our own messages are shown; a dropped connection or an HTML reply (bot challenge, 5xx)
          // used to print the engine's text — "Load failed", "The string did not match the expected pattern".
          showSubmitError(err && err.ameFriendly ? err.message : FRIENDLY_CART_ERROR);
        });
    });
  }

  /* ---- configuration persistence (2026-10-01) ----
     A reload, or Back from the cart when the browser does not keep the page in its back/forward cache, used to
     return every control to its default: six option groups, three accessories, Seat Type and Seat Size on the EVO
     613 — gone. The picks are now kept in sessionStorage (this tab only, this product only, 12 h, no personal data)
     and re-applied THROUGH THE NATIVE CONTROLS with a 'change' event, exactly as the shopper made them, so every
     rule above (variant resolution, heel-loop compatibility, size exclusions, one rear accessory) runs again and
     nothing stale can reach the cart: a saved value that no longer exists or is now disabled is skipped. A
     ?variant= in the URL wins over a saved variant. The shopper is told, and can start over. */
  var STORE_KEY = 'ame:cfg:v1:' + ((island.product && island.product.id) || parentHandle || location.pathname);
  var storeOK = (function () { try { sessionStorage.setItem('ame:cfg:t', '1'); sessionStorage.removeItem('ame:cfg:t'); return true; } catch (_) { return false; } })();
  var booted = false, restoring = false, addedOnce = false;
  function snapshot() {
    var s = { t: Date.now(), v: state.selectedVariantId, q: state.quantity, sel: {}, li: {}, ex: {}, ax: {}, chk: [], rad: [], col: null, fab: (state.fabric && state.fabric.name) || null, added: addedOnce };
    selects.forEach(function (sel) { if (sel.id && sel.value && sel.value !== '0') s.sel[sel.id] = sel.value; });
    Array.prototype.forEach.call(panel.querySelectorAll('[data-lineitem-select]'), function (el) { if (el.value) s.li[el.getAttribute('data-lineitem-label') || el.id] = el.value; });
    Array.prototype.forEach.call(panel.querySelectorAll('select[data-extra-option]'), function (el) { if (el.value) s.ex[el.getAttribute('data-option-position') || el.id] = el.value; });
    Array.prototype.forEach.call(panel.querySelectorAll('[data-axis-radio]:checked'), function (el) { var g = el.closest('[data-axis-cards]'); s.ax[(g && g.getAttribute('data-option-position')) || el.name] = el.value; });
    var col = panel.querySelector('[data-color-radio]:checked'); if (col) s.col = col.value;
    // 2026-10-02: identity = variant id + position among controls with that id. Two controls can share a variant (the
    // PLR-5900 lists its cleaning kit from both accessory sources); matching by id alone restored BOTH, +$50.
    var seenChk = {};
    checkboxes.forEach(function (b) {
      var vid = b.getAttribute('data-variant-id'); if (!vid) return;
      var k = seenChk[vid] = (seenChk[vid] || 0) + 1;
      if (b.getAttribute('aria-pressed') === 'true') s.chk.push(k > 1 ? vid + '#' + k : vid);
    });
    // radio groups (lift installation, …): identity is the POSITION in the group plus the variant behind it —
    // several radios share a value, so name+value would re-check the wrong one
    var seenNames = {};
    Array.prototype.forEach.call(panel.querySelectorAll('[data-addon-radio]'), function (el) {
      if (!el.name || seenNames[el.name]) return; seenNames[el.name] = true;
      var group = Array.prototype.filter.call(panel.querySelectorAll('[data-addon-radio]'), function (x) { return x.name === el.name; });
      for (var i = 0; i < group.length; i++) { if (group[i].checked) { s.rad.push([el.name, i, group.length, group[i].getAttribute('data-variant-id') || '']); break; } }
    });
    return s;
  }
  function saveState(added) {
    if (!storeOK || !booted || restoring) return;
    if (added) addedOnce = true;
    try { sessionStorage.setItem(STORE_KEY, JSON.stringify(snapshot())); } catch (_) { /* quota / private mode */ }
  }
  // "already in your cart" holds only until the shopper changes something
  panel.addEventListener('change', function () { if (!restoring) addedOnce = false; }, true);
  panel.addEventListener('click', function (e) { if (!restoring && e.target.closest && e.target.closest('[data-addon-checkbox], [data-fabric-option]')) addedOnce = false; }, true);
  function restoreState() {
    if (!storeOK) return;
    var s = null;
    try { s = JSON.parse(sessionStorage.getItem(STORE_KEY) || 'null'); } catch (_) { s = null; }
    if (!s || !s.t || Date.now() - s.t > 12 * 3600 * 1000) return;
    var applied = 0;
    var fire = function (el) { el.dispatchEvent(new Event('change', { bubbles: true })); };
    var setSelect = function (el, val) {
      if (!el || val == null || el.value === val) return;
      var opt = Array.prototype.filter.call(el.options, function (o) { return o.value === val; })[0];
      if (!opt || opt.disabled) return;
      el.value = val; fire(el); applied++;
    };
    var setRadio = function (el) { if (el && !el.disabled && !el.checked) { el.checked = true; fire(el); applied++; } };
    var byValue = function (list, val) { return Array.prototype.filter.call(list, function (x) { return x.value === val; })[0]; };
    restoring = true;
    try {
      if (!/[?&]variant=\d+/.test(location.search)) {
        Array.prototype.forEach.call(panel.querySelectorAll('select[data-extra-option]'), function (el) { setSelect(el, s.ex && s.ex[el.getAttribute('data-option-position') || el.id]); });
        if (s.col) setRadio(byValue(panel.querySelectorAll('[data-color-radio]'), s.col));
        Array.prototype.forEach.call(panel.querySelectorAll('[data-axis-cards]'), function (g) {
          var want = s.ax && s.ax[g.getAttribute('data-option-position')]; if (want) setRadio(byValue(g.querySelectorAll('[data-axis-radio]'), want));
        });
        if (s.v && variantSelect && variantSelect.tagName === 'SELECT') setSelect(variantSelect, String(s.v));
        if (s.v) setRadio(byValue(panel.querySelectorAll('[data-variant-card-radio]'), String(s.v)));
      }
      Array.prototype.forEach.call(panel.querySelectorAll('[data-lineitem-select]'), function (el) { setSelect(el, s.li && s.li[el.getAttribute('data-lineitem-label') || el.id]); });
      selects.forEach(function (sel) { if (sel.id && s.sel && s.sel[sel.id]) setSelect(sel, s.sel[sel.id]); });   // DOM order: leg rests before heel loops
      (s.rad || []).forEach(function (rec) {
        var group = Array.prototype.filter.call(panel.querySelectorAll('[data-addon-radio]'), function (x) { return x.name === rec[0]; });
        var el = group[rec[1]];
        if (group.length === rec[2] && el && (el.getAttribute('data-variant-id') || '') === rec[3]) setRadio(el);
      });
      // both directions: an accessory that is on by default and was switched off stays off
      var seenRestore = {};
      checkboxes.forEach(function (b) {
        var vid = b.getAttribute('data-variant-id'); if (!vid) return;
        var k = seenRestore[vid] = (seenRestore[vid] || 0) + 1;
        var want = (s.chk || []).indexOf(k > 1 ? vid + '#' + k : vid) !== -1;   // a plain id names the first control only
        var on = b.getAttribute('aria-pressed') === 'true';
        if (want !== on && !b.disabled) { b.click(); applied++; }
      });
      if (s.fab && fabricPicker && !(state.fabric && state.fabric.name)) {
        var fo = Array.prototype.filter.call(fabricPicker.querySelectorAll('[data-fabric-option]'), function (o) { return o.getAttribute('data-fabric-name') === s.fab; })[0];
        if (fo) { fo.click(); if (state.fabric && state.fabric.name === s.fab) applied++; }
      }
      if (qtyInput && s.q > 1 && String(s.q) !== qtyInput.value) { qtyInput.value = String(s.q); fire(qtyInput); applied++; }
    } finally { restoring = false; }
    if (!applied) return;
    addedOnce = !!s.added;
    recompute();
    var note = document.createElement('p');
    note.className = 'ame-customize__restored'; note.setAttribute('data-restore-note', ''); note.setAttribute('role', 'status');
    note.appendChild(document.createTextNode(s.added ? 'Your last configuration is shown here — it is already in your cart. ' : 'Your earlier selections were restored. '));
    var again = document.createElement('button');
    again.type = 'button'; again.className = 'ame-customize__restart'; again.setAttribute('data-restore-reset', ''); again.textContent = 'Start over';
    again.addEventListener('click', function () { try { sessionStorage.removeItem(STORE_KEY); } catch (_) {} booted = false; window.location.reload(); });
    note.appendChild(again);
    var host = (form || panel).querySelector('.ame-customize__reqstatus, .ame-customize__section-divider, .ame-customize__row');
    if (host && host.parentNode) host.parentNode.insertBefore(note, host); else panel.insertBefore(note, panel.firstChild);
    track('customizer_restore', { restored_count: applied });
  }
  // Back/forward cache: the page comes back exactly as it was left — including "Adding..." on a disabled button
  // (the add succeeded and navigated to the cart). Put the button back and re-run the totals.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;
    if (atcBtn) { delete atcBtn.dataset.adding; atcBtn.textContent = atcBtn.dataset._label || 'Add to Cart'; }
    recompute();
  });

  /* ---- init ---- */
  syncOptionExclusions();
  syncFabricExclusions();
  recompute();
  restoreState();
  booted = true;
  saveState();
})();
